export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  // Structured error object (FastAPI HTTPException(detail={...})). We
  // prefer `message` (our own convention) over `msg` (pydantic's
  // convention) so security-critical errors like login_locked render
  // friendly user-facing copy instead of "[object Object]".
  if (typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
  }
  return String(detail);
}

/**
 * Build an in-app PDF-viewer route for a given PDF URL.
 *
 * Use this instead of `window.open(url, "_blank")` for any document
 * the user might tap from a mobile/PWA context: it routes through
 * `/pdf-viewer` which renders a sticky "← Back / Done" button —
 * critical for iOS PWAs where Safari's native Done overlay is
 * suppressed in `display-mode: standalone`.
 *
 * Usage:
 *   <Link to={pdfViewerRoute(certUrl, "Registration certificate", `cert-${id}.pdf`)}>
 *     Download
 *   </Link>
 *
 * Or imperatively:
 *   navigate(pdfViewerRoute(certUrl, "Pedigree", "pedigree.pdf"));
 */
/**
 * Build a `/pdf-viewer?src=…&title=…&dl=…` URL.
 *
 * @deprecated 2026-05-27 — Replaced by `<PdfModal />` (portal overlay)
 *   for all cert/pedigree download flows. Route still works for any
 *   historical bookmarks but new callers should prefer the modal:
 *
 *     import PdfModal from "@/components/PdfModal";
 *     const [open, setOpen] = useState(false);
 *     <button onClick={() => setOpen(true)}>…</button>
 *     <PdfModal open={open} onClose={() => setOpen(false)}
 *               src={url} title="…" downloadName="…" />
 *
 *   The modal avoids the iOS PWA history-stack quirks that left users
 *   stranded on /pdf-viewer with a Back button that did nothing.
 */
export function pdfViewerRoute(src, title, downloadName) {
  const params = new URLSearchParams();
  if (src) params.set("src", src);
  if (title) params.set("title", title);
  if (downloadName) params.set("dl", downloadName);
  return `/pdf-viewer?${params.toString()}`;
}


/**
 * Fetch an authenticated PDF endpoint and trigger a browser download.
 *
 * Why this exists: the in-app `<PdfViewer>` uses pdfjs.getDocument({url})
 * which can't send the `Authorization: Bearer …` header — so it works
 * for public PDFs but not for `/api/me/consents/executed.pdf` or
 * `/api/admin/data/users/{id}/executed-agreement.pdf`. This helper:
 *   1. Fetches the protected endpoint as a blob using the axios client
 *      (which carries the JWT automatically via the interceptor).
 *   2. Reads the suggested filename from Content-Disposition (server
 *      always sets it).
 *   3. Triggers a native browser download via an anonymous <a download>.
 *
 * Returns the resolved filename so callers can toast it. Throws the
 * original axios error so callers can use `formatApiError` etc.
 */
export async function downloadAuthenticatedPdf(api, urlPath, fallbackName = "document.pdf") {
  const res = await api.get(urlPath, { responseType: "blob" });
  let filename = fallbackName;
  const cd = res.headers?.["content-disposition"] || res.headers?.get?.("content-disposition");
  if (cd) {
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    if (m && m[1]) filename = decodeURIComponent(m[1]);
  }
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return filename;
}




export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function buildFileUrl(ref) {
  if (!ref) return "";
  if (ref.startsWith("data:") || ref.startsWith("http://") || ref.startsWith("https://")) {
    return ref;
  }
  const token = localStorage.getItem("token") || "";
  return `${BACKEND}/api/files/${ref}?auth=${encodeURIComponent(token)}`;
}

/**
 * Public file URL — for assets that the backend allows anonymous access to
 * (e.g., event covers). Does NOT append auth token, so works for non-logged-in
 * visitors and benefits from public CDN caching.
 */
export function publicFileUrl(ref) {
  if (!ref) return "";
  if (ref.startsWith("data:") || ref.startsWith("http://") || ref.startsWith("https://")) {
    return ref;
  }
  return `${BACKEND}/api/files/${ref}`;
}

/**
 * Resolve a photo reference to a displayable URL (full-size).
 */
export function photoUrl(ref) {
  return buildFileUrl(ref);
}

/**
 * Thumbnail URL — if the main ref is a storage path (not base64/http), append `.thumb.jpg`.
 * Falls back to the full-size URL for legacy assets.
 */
export function thumbUrl(ref) {
  if (!ref) return "";
  if (ref.startsWith("data:") || ref.startsWith("http://") || ref.startsWith("https://")) {
    return ref;
  }
  // strip extension and append .thumb.jpg
  const idx = ref.lastIndexOf(".");
  const thumbPath = idx > 0 ? `${ref.slice(0, idx)}.thumb.jpg` : `${ref}.thumb.jpg`;
  return buildFileUrl(thumbPath);
}

// Match a plain date-only string with no time-of-day information.
// Examples that should be treated as wall-clock dates (not UTC instants):
//   "2023-01-13"
//   "2023-01-13T00:00:00"
//   "2023-01-13T00:00:00Z"
//   "2023-01-13T00:00:00.000+00:00"
// Without this, `new Date("2023-01-13")` parses as UTC midnight, then
// `toLocaleDateString` shifts it back into the local timezone — every
// user west of UTC sees the date as one day earlier (e.g. a dog born
// on Jan 13 displays as "Jan 12" for anyone in the Americas).
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)?)?$/;

export function formatDate(iso, withTime = false) {
  if (!iso) return "—";
  try {
    // Date-only values (e.g. a dog's DOB) must be formatted as
    // wall-clock dates so the calendar day matches what the owner
    // entered, regardless of viewer timezone.
    const m = typeof iso === "string" && DATE_ONLY_RE.exec(iso);
    if (m && !withTime) {
      // Construct in local time so locale formatting doesn't shift it.
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return d.toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      });
    }
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const opts = withTime
      ? { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }
      : { year: "numeric", month: "short", day: "numeric" };
    return d.toLocaleDateString(undefined, opts);
  } catch {
    return iso;
  }
}
